import React, { useCallback, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from '../../t';
import { useNavigate } from 'react-router-dom';
import withAuth from '../../HOC/withAuth';
import C from '../../colors';
import { formatMoney, toCents } from '../../money';

function Icon({ name, size = 16 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'plus')   return <svg viewBox="0 0 24 24" {...s}><path d="M12 5v14M5 12h14"/></svg>;
  if (name === 'edit')   return <svg viewBox="0 0 24 24" {...s}><path d="M4 20h4l11-11-4-4L4 16zM14 5l4 4"/></svg>;
  if (name === 'arrow')  return <svg viewBox="0 0 24 24" {...s}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
  if (name === 'more')   return <svg viewBox="0 0 24 24" {...s}><circle cx="5" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="19" cy="12" r="1.2" fill="currentColor"/></svg>;
  return null;
}

function PrimaryBtn({ children, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '9px 16px', borderRadius: 7,
      background: C.brand, color: '#fff', border: 'none',
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 7,
      boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={14}/>}
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '8px 12px', borderRadius: 7,
      background: '#fff', color: C.ink,
      border: `1px solid ${C.hair2}`,
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
      whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={14}/>}
      {children}
    </button>
  );
}

function StatusPill({ tone = 'neutral', children }) {
  const p = {
    success: { bg: C.successBg, color: C.successFg, dot: C.successFg },
    warm:    { bg: C.warningBg, color: C.warningFg, dot: C.warningDot },
    neutral: { bg: C.neutralBg, color: C.neutralFg, dot: C.neutralDot },
    muted:   { bg: 'transparent', color: C.ink3, dot: C.ink3, border: `1px solid ${C.hair}` },
  }[tone] || { bg: C.neutralBg, color: C.neutralFg, dot: C.neutralDot };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 8px', borderRadius: 10,
      background: p.bg, color: p.color, border: p.border || 'none',
      fontSize: 11, fontWeight: 600, letterSpacing: 0.2, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 3, background: p.dot }}/>
      {children}
    </span>
  );
}

function KPI({ label, value, hint, accent }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: C.serif, fontSize: 32, lineHeight: 1, color: accent ? C.brand2 : C.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: C.ink3, marginTop: 6, fontFamily: C.mono, letterSpacing: 0.4 }}>{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 2px', minWidth: 0 }}>
      <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, fontFamily: C.serif, fontSize: 20, lineHeight: 1, color: color || C.ink, fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {sub && <span style={{ fontSize: 11, color: C.ink3, fontFamily: C.mono }}>{sub}</span>}
      </div>
    </div>
  );
}

function OrderBreakdown({ orders }) {
  const total = orders?.total || 1;
  const segs = [
    { key: 'active', label: '进行中', value: orders?.active || 0, color: C.brand },
    { key: 'completed', label: '已完成', value: orders?.completed || 0, color: '#3F8F5F' },
    { key: 'draft', label: '草稿', value: orders?.draft || 0, color: C.hair2 },
  ];
  return (
    <div>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: C.paper2, border: `1px solid ${C.hair}` }}>
        {segs.map(s => s.value > 0 && (
          <div key={s.key} style={{ width: (s.value / total) * 100 + '%', background: s.color }}/>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontFamily: C.mono, fontSize: 10, color: C.ink3, letterSpacing: 0.4 }}>
        {segs.map(s => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color }}/>
            <span style={{ color: C.ink2, fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
            <span>{s.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function PlanCard({ pkg, onDelete, onEdit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const archived = pkg.status === 'archived';
  const orders = pkg.orders || {};
  const features = pkg.features || [
    `${t('Photo Limit')}: ${pkg.photoCount} ${t('pics')}`,
    `¥${formatMoney(pkg.price)}`,
  ];

  return (
    <article data-testid={`package-card-${pkg.id}`} style={{
      background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
      padding: '24px 26px 22px', transition: 'border-color .15s',
      opacity: archived ? 0.78 : 1, position: 'relative', overflow: 'hidden',
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = C.hair2}
    onMouseLeave={e => e.currentTarget.style.borderColor = C.hair}>
      {pkg.isDefault && (
        <div style={{ position: 'absolute', top: 0, right: 0, padding: '4px 10px 4px 14px', background: C.brand, color: '#fff', fontFamily: C.mono, fontSize: 9, letterSpacing: 1.4, textTransform: 'uppercase', borderBottomLeftRadius: 8 }}>
          默认套餐
        </div>
      )}

      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            {pkg.status === 'active' && <StatusPill tone="success">启用</StatusPill>}
            {pkg.status === 'archived' && <StatusPill tone="muted">已归档</StatusPill>}
          </div>
          <h2 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 28, lineHeight: 1.1, color: C.ink, letterSpacing: -0.3 }}>
            {pkg.name}
          </h2>
          {pkg.description && (
            <div style={{ marginTop: 5, fontSize: 13, color: C.ink2 }}>{pkg.description}</div>
          )}
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, marginBottom: 4 }}>{t('Price')}</div>
          <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, fontFamily: C.serif, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontSize: 18, color: C.ink3 }}>¥</span>
            <span style={{ fontSize: 42, lineHeight: 1 }}>{pkg.price}</span>
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, letterSpacing: 0.4, marginTop: 2 }}>
            {t('Photo Limit')}: {pkg.photoCount}
          </div>
        </div>
      </header>

      {(orders.total != null || pkg.revenue != null) && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 26,
          alignItems: 'end', padding: '16px 0 18px',
          borderTop: `1px solid ${C.hair}`, borderBottom: `1px solid ${C.hair}`,
          marginBottom: 16,
        }}>
          <div>
            <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, marginBottom: 6 }}>使用订单</div>
            <div style={{ fontFamily: C.serif, fontSize: 38, lineHeight: 1, color: C.ink, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              {orders.total || 0}
              <span style={{ fontSize: 14, color: C.ink3, fontFamily: C.mono }}>单</span>
            </div>
          </div>
          {orders.total > 0 && (
            <div style={{ minWidth: 180, alignSelf: 'center' }}>
              <OrderBreakdown orders={orders}/>
            </div>
          )}
          {pkg.revenue != null && (
            <MiniStat label="累计收入" value={<>¥{formatMoney(pkg.revenue)}</>} color={C.brand2}/>
          )}
          {pkg.avgSelected != null && (
            <MiniStat label="平均选片" value={(pkg.avgSelected || 0).toFixed(1)} sub={`/ ${pkg.photoCount}`}/>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: C.ink3 }}>
          {pkg.createdAt && <span>创建 <span style={{ color: C.ink2 }}>{new Date(pkg.createdAt).toLocaleDateString('zh-CN')}</span></span>}
          {pkg.lastSold && (
            <>
              <span style={{ width: 1, height: 12, background: C.hair, alignSelf: 'center' }}/>
              <span>最近售出 <span style={{ color: C.ink2 }}>{pkg.lastSold}</span></span>
            </>
          )}
          {features.length > 0 && (
            <>
              <span style={{ width: 1, height: 12, background: C.hair, alignSelf: 'center' }}/>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {features.slice(0, 2).map((f, i) => (
                  <span key={i} style={{ padding: '2px 7px', borderRadius: 4, background: C.paper2, color: C.ink2, fontSize: 11 }}>{f}</span>
                ))}
              </span>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {orders.total > 0 && (
            <GhostBtn icon="arrow" onClick={() => navigate('/user/orders')}>查看订单</GhostBtn>
          )}
          <GhostBtn icon="edit" onClick={() => onEdit?.(pkg.id)}>{t('Edit')}</GhostBtn>
          <button onClick={() => onDelete?.(pkg.id)} style={{
            width: 34, height: 34, borderRadius: 7,
            border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink3,
            cursor: 'pointer', display: 'grid', placeItems: 'center',
          }} title={t('Delete')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1.2 13a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4L18 7"/></svg>
          </button>
        </div>
      </div>
    </article>
  );
}

const PackageList = inject('packageStore', 'dialogStore')(observer(({ packageStore, dialogStore }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  useEffect(() => { packageStore.fetchPackages(); }, [packageStore]);

  const packages = packageStore.packages || [];
  const filtered = filter === 'all' ? packages : packages.filter(p => p.status === filter);

  const activePlans = packages.filter(p => p.status === 'active').length;
  const archivedPlans = packages.filter(p => p.status === 'archived').length;
  const totalOrders = packages.reduce((s, p) => s + (p.orders?.total || 0), 0);
  const revenue = packages.reduce((cents, p) => cents + toCents(p.revenue), 0) / 100;

  const handleDelete = useCallback((id) => {
    dialogStore.openDialog(t("Are you sure you want to delete this package?"), () => {
      packageStore.deletePackage(id);
    });
  }, [packageStore, dialogStore, t]);

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <div style={{ padding: '28px 36px 48px', maxWidth: 1340, margin: '0 auto' }}>

        {/* ── PAGE HEAD ── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 38, lineHeight: 1, color: C.ink, letterSpacing: -0.5 }}>
              {t('Packages')}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <PrimaryBtn icon="plus" onClick={() => navigate('/user/packages/create')}>{t('Create Package')}</PrimaryBtn>
          </div>
        </div>

        {/* ── KPI STRIP ── */}
        <div style={{
          background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
          padding: '20px 26px', marginBottom: 22,
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 28, alignItems: 'center',
        }}>
          <KPI label="启用 / 全部" value={<>{activePlans}<span style={{ color: C.ink3, fontSize: 20 }}> / {packages.length}</span></>} hint={archivedPlans > 0 ? `${archivedPlans} 个已归档` : undefined}/>
          <KPI label="累计订单" value={totalOrders}/>
          <KPI label="累计收入" value={<>¥{formatMoney(revenue)}</>} hint="所有套餐合计" accent/>
          <KPI label="套餐数量" value={packages.length} hint={`${activePlans} 启用中`}/>

          <div style={{ display: 'flex', padding: 3, gap: 2, background: C.paper2, borderRadius: 8, border: `1px solid ${C.hair}` }}>
            {[{ v: 'all', l: '全部' }, { v: 'active', l: '启用' }, { v: 'archived', l: '归档' }].map(t => (
              <button key={t.v} onClick={() => setFilter(t.v)} style={{
                padding: '6px 12px', borderRadius: 6, border: 'none',
                background: filter === t.v ? '#fff' : 'transparent',
                boxShadow: filter === t.v ? `0 1px 0 ${C.hair2}` : 'none',
                color: filter === t.v ? C.ink : C.ink3,
                fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* ── LIST HEADER ── */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 4px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h2 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 22, color: C.ink }}>{t('Package List')}</h2>
            <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, letterSpacing: 0.4 }}>{filtered.length} 个套餐</span>
          </div>
        </div>

        {/* ── CARDS ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(p => (
            <PlanCard key={p.id} pkg={p} onDelete={handleDelete} onEdit={(id) => navigate(`/user/packages/${id}/edit`)}/>
          ))}

          {filtered.length === 0 && (
            <div style={{ padding: '80px 0', textAlign: 'center', border: `1px dashed ${C.hair2}`, borderRadius: 14, background: C.paper2 }}>
              <div style={{ fontFamily: C.serif, fontSize: 28, color: C.ink2 }}>{t("No packages available.")}</div>
              <div style={{ marginTop: 16 }}>
                <PrimaryBtn icon="plus" onClick={() => navigate('/user/packages/create')}>{t('Create Package')}</PrimaryBtn>
              </div>
            </div>
          )}

          <button onClick={() => navigate('/user/packages/create')} style={{
            border: `1.5px dashed ${C.hair2}`, background: 'transparent', borderRadius: 14,
            padding: '28px', color: C.ink3, cursor: 'pointer', fontFamily: C.sans,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'all .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.brand; e.currentTarget.style.color = C.brand2; e.currentTarget.style.background = C.brandSoft; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.hair2; e.currentTarget.style.color = C.ink3; e.currentTarget.style.background = 'transparent'; }}>
            <Icon name="plus" size={16}/>
            <span style={{ fontSize: 13 }}>{t('Create Package')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}));

export default withAuth(PackageList);
